// "Upload merge answers" (kitchen flow, go-live; admin): the client's
// brand_merge_review.csv with Y / N per item. The server does a dry run first
// (everything, then rolled back) and shows per group what will happen; Apply
// runs the same answers for real. Strings: lib/inventoryFlow/mergeStrings.ts.
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, FileUp, Loader2 } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { MG } from '@/lib/inventoryFlow/mergeStrings'
import { canApplyBulk, mergedNames, sortOutcomes } from '@/lib/inventoryFlow/mergeLogic'
import type { MergeBulkSummary } from '@/lib/inventoryFlow/mergeTypes'

const STOCK_KEYS = ['stockItems', 'stockAlerts', 'stockSummary', 'advancedStockReport', 'admin-stock-item-options', 'inventory-flow']
const MAX_BYTES = 2 * 1024 * 1024

const statusCls: Record<string, string> = {
  merge: 'text-success-fg',
  merged: 'text-success-fg',
  refused: 'text-danger-fg font-medium',
  already_merged: 'text-muted-foreground',
  skipped: 'text-muted-foreground',
}

export function MergeAnswersDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [summary, setSummary] = useState<MergeBulkSummary | null>(null)
  const [busy, setBusy] = useState<'check' | 'apply' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [key, setKey] = useState(newIdempotencyKey)

  const reset = () => {
    setFile(null); setSummary(null); setError(null); setBusy(null); setKey(newIdempotencyKey())
    if (fileRef.current) fileRef.current.value = ''
  }
  const close = () => { reset(); onClose() }
  const msg = (e: unknown) => (e instanceof ApiClientError ? e.message.split(' — ')[0] : (e as Error).message)

  const check = async (f: File) => {
    setBusy('check')
    setError(null)
    setSummary(null)
    try {
      const r = await apiClient.bulkMergeItems(f, true)
      setSummary(r.data ?? null)
    } catch (e) {
      setError(msg(e))
    } finally {
      setBusy(null)
    }
  }

  const apply = async () => {
    if (!file) return
    setBusy('apply')
    setError(null)
    try {
      const r = await apiClient.bulkMergeItems(file, false, key)
      for (const k of STOCK_KEYS) qc.invalidateQueries({ queryKey: [k] })
      setSummary(r.data ?? null)
      toastHelpers.success('Inventory', MG.bulk.summaryDone(r.data?.groups_merged ?? 0, r.data?.items_merged ?? 0))
    } catch (e) {
      setError(msg(e))
    } finally {
      setBusy(null)
    }
  }

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    setSummary(null)
    setError(null)
    if (!picked) return
    if (picked.size > MAX_BYTES) {
      setError('That file is too big for a review sheet.')
      return
    }
    setFile(picked)
    setKey(newIdempotencyKey())
    void check(picked)
  }

  const rows = summary ? sortOutcomes(summary) : []

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{MG.bulk.title}</DialogTitle>
          <DialogDescription>{MG.bulk.help}</DialogDescription>
        </DialogHeader>

        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onPick} data-testid="merge-answers-file" />
        <Button type="button" className="w-full sm:w-auto" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
          <FileUp className="mr-2 h-4 w-4" /> {file ? file.name : MG.bulk.choose}
        </Button>

        {busy === 'check' && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {MG.bulk.checking}</p>}
        {error && (
          <p className="flex items-start gap-2 rounded-md bg-danger-surface/50 px-3 py-2 text-sm text-danger-fg">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </p>
        )}

        {summary && (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${summary.groups_merged > 0 ? 'bg-success-surface/50 text-success-fg' : 'bg-muted text-muted-foreground'}`}>
              <CheckCircle2 className="h-4 w-4" />
              {summary.dry_run ? MG.bulk.summary(summary.groups_merged, summary.items_merged) : MG.bulk.summaryDone(summary.groups_merged, summary.items_merged)}
            </div>
            {MG.bulk.others(summary.groups_refused, summary.groups_skipped, summary.groups_already_merged, summary.rows_not_answered) && (
              <p className="text-sm text-muted-foreground">
                {MG.bulk.others(summary.groups_refused, summary.groups_skipped, summary.groups_already_merged, summary.rows_not_answered)}
              </p>
            )}
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2">{MG.bulk.colGroup}</th>
                    <th className="px-2 py-2">{MG.bulk.colKeep}</th>
                    <th className="px-2 py-2">{MG.bulk.colMerged}</th>
                    <th className="px-2 py-2">{MG.bulk.colResult}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((o) => (
                    <tr key={o.group} className={o.status === 'refused' ? 'bg-danger-surface/30' : undefined}>
                      <td className="px-2 py-2 font-medium">{o.group}</td>
                      <td className="px-2 py-2">
                        {o.renamed_to ?? o.survivor ?? o.keep_name}
                        {o.survivor && (
                          <span className="block text-xs text-muted-foreground">
                            {o.survivor}{o.chosen_by ? ` — ${MG.bulk.chosenBy[o.chosen_by]}` : ''}{o.renamed_to ? `, ${MG.bulk.renamed(o.renamed_to)}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs">{mergedNames(o).join(', ') || '—'}</td>
                      <td className={`px-2 py-2 text-xs ${statusCls[o.status] ?? ''}`}>
                        {MG.bulk.status[o.status]}
                        {o.reason && <span className="block font-normal">{o.reason}</span>}
                        {o.merges.flatMap((m) => m.warnings).map((w) => <span key={w} className="block text-warning-fg">{w}</span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {summary.dry_run && (
              canApplyBulk(summary) ? (
                <Button type="button" className="w-full" disabled={busy !== null} onClick={() => void apply()}>
                  {busy === 'apply' ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {MG.bulk.applying}</> : MG.bulk.apply(summary.items_merged)}
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">{MG.bulk.nothingToApply}</p>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
